import { IMemberDetailsData } from 'fitbeat.gym-core';
import { Gym, MembersReportData, ReportType } from 'fitbeat.models';
import * as React from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { currentGymSelector } from '../adminSettings/selector';
import { IAppStore } from '../rootReducer';
import { buildLoadingProgressMessage, Loading } from '../shared/loading';
import { getAllData, getAllWorkouts, getSavedReports, ILoadProgress, loadDashboardActiveReport } from './actions';
import { MemberTableError } from './components';
import { DashboardReport } from './components/dashboardReport';
import { DashboardReportTile } from './components/DashboardReportTile';
import { numberOfRowsInReportsSelector } from './selector';

interface IProps {
  excludedEmails: string;
  error: string | null;
  isFetching: boolean;
  loadingMembers: boolean;
  currentGym: Gym;
  activeDashboardActiveReport: MembersReportData;
  memberDetailsData: IMemberDetailsData[];
  dashboardMemberReportData: MembersReportData[];
  numberOfRowsInReports: Map<string, number>;
  loadingWorkouts: boolean;
  fetchedWorkoutsForGymName?: string;
  memberLoadProgress: ILoadProgress;
  workoutLoadProgress: ILoadProgress;
  getAllData(excludedEmails: string, gymName: string): void;
  getSavedReports(): void;
  getAllWorkouts(gymName: string): void;
  loadDashboardActiveReport(report: MembersReportData): void;
}

class Dashboard extends React.Component<IProps> {
  public componentDidMount() {
    if (this.props.dashboardMemberReportData.length === 0) {
      this.props.getSavedReports();
    }
    if (this.props.memberDetailsData.length === 0) {
      this.props.getAllData(this.props.excludedEmails, this.props.currentGym.name);
    }
    if (this.props.fetchedWorkoutsForGymName !== this.props.currentGym.name) {
      this.props.getAllWorkouts(this.props.currentGym.name);
    }
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.currentGym.name !== this.props.currentGym.name
      && this.props.fetchedWorkoutsForGymName !== this.props.currentGym.name) {
      this.props.getAllWorkouts(this.props.currentGym.name);
    }
  }

  public render() {
    const {
      error, isFetching, loadingMembers, excludedEmails, currentGym, loadingWorkouts,
      activeDashboardActiveReport, dashboardMemberReportData, memberDetailsData,
      numberOfRowsInReports, memberLoadProgress, workoutLoadProgress,
    } = this.props;

    const isLoading = !error && (isFetching
      || loadingMembers
      || loadingWorkouts
      || dashboardMemberReportData.length === 0
      || memberDetailsData.length === 0);

    return (
      <div className='app-wrapper'>
        {error && (
          <MemberTableError reload={() => this.props.getAllData(excludedEmails, currentGym.name)} />
        )}
        {isLoading && (
          <div className='loader-container'>
            <Loading
              fullHeight
              message={this.getLoadingMessage(memberLoadProgress, workoutLoadProgress)}
            />
          </div>
        )}
        {!isLoading && <h2 className='dashboard-heading'>Dashboard</h2>}
        <div className='dashbaord-repo'>
          {!isLoading && dashboardMemberReportData.map((report) => (
            <DashboardReportTile
              key={report.reportId}
              report={report}
              isActive={report.reportId === activeDashboardActiveReport.reportId}
              numberOfRowsInReport={numberOfRowsInReports.get(report.reportId) ?? 0}
              onClick={this.handleTileClick}
            />
          ))}
        </div>
        {!isLoading && <DashboardReport report={activeDashboardActiveReport} />}
      </div>
    );
  }

  private handleTileClick = (report: MembersReportData) => {
    this.props.loadDashboardActiveReport(report);
  }

  private getLoadingMessage(memberLoadProgress: ILoadProgress, workoutLoadProgress: ILoadProgress) {
    const memberMessage = (this.props.loadingMembers || this.props.memberDetailsData.length === 0)
      ? buildLoadingProgressMessage(memberLoadProgress, 'members')
      : undefined;
    const workoutMessage = this.props.loadingWorkouts
      ? buildLoadingProgressMessage(workoutLoadProgress, 'workouts')
      : undefined;
    const messages = [memberMessage, workoutMessage].filter((message): message is string => Boolean(message));

    if (messages.length === 0) {
      return undefined;
    }

    return (
      <>
        {messages.map((message) => <div key={message}>{message}</div>)}
      </>
    );
  }
}

const sortedDashboardReportsSelector = createSelector(
  [(state: IAppStore) => state.members.data.dashboardMemberReportData],
  (reports) => [
    ...reports.filter((r) => r.reportType === ReportType.members),
    ...reports.filter((r) => r.reportType !== ReportType.members),
  ],
);

const mapDispatchToProps = {
  getAllData,
  getSavedReports,
  getAllWorkouts,
  loadDashboardActiveReport,
};

const mapStateToProps = (state: IAppStore) => ({
  excludedEmails: state.adminSettings.data.excludedEmails,
  error: state.members.view.error,
  isFetching: state.members.view.isFetching,
  loadingMembers: state.members.view.loadingMembers,
  loadingWorkouts: state.members.view.loadingWorkouts,
  memberLoadProgress: state.members.view.memberLoadProgress,
  workoutLoadProgress: state.members.view.workoutLoadProgress,
  currentGym: currentGymSelector(state),
  activeDashboardActiveReport: state.members.data.activeDashboardActiveReport,
  memberDetailsData: state.members.data.memberDetailsData,
  fetchedWorkoutsForGymName: state.members.data.fetchedWorkoutsForGymName,
  dashboardMemberReportData: sortedDashboardReportsSelector(state),
  numberOfRowsInReports: numberOfRowsInReportsSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
